/** Private mail review. Isolated wording assistance; no diagnostics/offline cache.
 * A proposal is an immutable server-held snapshot. Approval sends its opaque
 * token, never editable message fields. Uncertain sends are status-checked.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import MailDraftAssistant from "./MailDraftAssistant";
import MailRecipient from "./MailRecipient";
import { useAuth } from "../../context/AuthContext";

const BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/mail-connections/icloud/write`;
const client = axios.create({ withCredentials: true, timeout: 60000,
  headers: { "X-NDG-Mail-Review": "browser-v1", "Cache-Control": "no-store" } });
const text = (value) => typeof value === "string" ? value : "";
import { isMailReference as validId } from "../../utils/mailReaderReference";
const button = "karen-panel-button";
const fieldStyle = { width: "100%", background: "transparent", color: "inherit", border: "1px solid #64748b", borderRadius: ".35rem", padding: ".7rem", font: "inherit" };

function safeError(error, fallback) {
  const status = error?.response?.status;
  if (status === 401) return "Your NDG sign-in expired. Sign in again.";
  if (status === 403) return "This action is not enabled for your account or this connection. Check email permissions.";
  if (status === 409) return "This review or connection changed or expired. Close it and prepare a fresh review.";
  if (status === 429) return "Email is busy or the review limit was reached. Nothing new was approved.";
  if (status === 422) return "Check the recipient, subject and message. Use one email address and a single-line subject.";
  return fallback;
}

function Composer({ sourceId, controlsOnly, expectedConnection, initialInstruction = "", initialRequestId = "", initialRecipient = "" }) {
  const [cap, setCap] = useState(null);
  const [mode, setMode] = useState(controlsOnly ? null : sourceId ? "reply" : "send");
  const [to, setTo] = useState(sourceId ? "" : text(initialRecipient));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [review, setReview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestKey = useRef(null);
  const mounted = useRef(true);
  const revision = useRef(0);
  const draftSeen = useRef(null);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    client.get(`${BASE}/capabilities`, { signal: controller.signal }).then(({ data }) => {
      if (data.provider !== "icloud" || typeof data.enabled !== "boolean" || !data.connection_version
          || (expectedConnection && expectedConnection !== data.connection_version)) throw new Error("Connection changed");
      if (mounted.current) setCap(data);
    }).catch((failure) => { if (mounted.current) setError(safeError(failure, "Could not check email-writing permissions. Reopen this editor to try again.")); });
    return () => { mounted.current = false; controller.abort(); };
  }, [expectedConnection]);

  useEffect(() => {
    if (mode !== "reply" || !validId(sourceId)) return undefined;
    const controller = new AbortController();
    const current = ++revision.current;
    setBusy(true);
    client.get(`${BASE}/reply-details/${encodeURIComponent(sourceId)}`, { signal: controller.signal }).then(({ data }) => {
      if (expectedConnection && data.connection_version !== expectedConnection) throw new Error("Mailbox changed");
      if (mounted.current && current === revision.current) { setTo(text(data.to)); setSubject(text(data.subject)); }
    }).catch((failure) => { if (mounted.current && current === revision.current) setError(safeError(failure, "Reply details could not be read. Reopen the message and try again.")); })
      .finally(() => { if (mounted.current && current === revision.current) setBusy(false); });
    return () => { controller.abort(); revision.current += 1; };
  }, [mode, sourceId, expectedConnection]);

  const edit = (setter, value) => { requestKey.current = null; setter(value); setError(""); };
  const setPermission = async (enabled) => {
    if (busy || !cap) return;
    setBusy(true); setError("");
    try {
      const { data } = await client.post(`${BASE}/permission`, { enabled, connection_version: cap.connection_version });
      if (mounted.current) { setCap(data); setReview(null); setReceipt(null); requestKey.current = null; }
    } catch (failure) { if (mounted.current) setError(safeError(failure, "Permission change was not confirmed.")); }
    finally { if (mounted.current) setBusy(false); }
  };

  const prepare = async (event, selectedMode = mode) => {
    event?.preventDefault?.();
    if (busy || !cap?.enabled || !selectedMode) return;
    setBusy(true); setError(""); setReceipt(null);
    try {
      if (!requestKey.current) requestKey.current = window.crypto.randomUUID();
      const sending = ["send", "reply"].includes(selectedMode);
      const { data } = await client.post(`${BASE}/prepare`, {
        request_id: requestKey.current, connection_version: cap.connection_version, operation: selectedMode,
        to: sending ? to.trim() : "", subject: sending ? subject : "", text: sending ? body : "",
        source_id: selectedMode === "send" ? null : sourceId,
      });
      if (mounted.current) {
        if (data.state === "prepared" && data.preview && data.approval_token) setReview(data);
        else setReceipt(data);
      }
    } catch (failure) { if (mounted.current) setError(safeError(failure, "The email review was not prepared. No send was approved.")); }
    finally { if (mounted.current) setBusy(false); }
  };

  const approve = async () => {
    if (busy || !review || (receipt && receipt.state !== "prepared")) return;
    setBusy(true); setError("");
    try {
      const { data } = await client.post(`${BASE}/proposals/${review.id}/approve`, {
        approved: true, approval_token: review.approval_token,
      });
      if (mounted.current) {
        setReceipt(data);
        if (["accepted", "completed", "failed", "cancelled"].includes(data.state)) { setReview(null); setBody(""); }
      }
    } catch (failure) {
      if (mounted.current) {
        // Even a timeout/HTTP failure can happen AFTER SMTP accepted. Never
        // invite another send or discard its reference. Checking is read-only.
        setReceipt({ state: "unknown", id: review.id,
          message: "The result has not been confirmed. Check status before sending anything again. NDG will not automatically resend." });
      }
    } finally { if (mounted.current) setBusy(false); }
  };

  const checkStatus = async () => {
    const id = review?.id || receipt?.id;
    if (busy || !id) return;
    setBusy(true); setError("");
    try {
      const { data } = await client.get(`${BASE}/proposals/${id}`);
      if (mounted.current) {
        setReceipt(data);
        if (["accepted", "completed", "failed", "cancelled"].includes(data.state)) { setReview(null); setBody(""); }
      }
    } catch (failure) { if (mounted.current) setError(safeError(failure, "Status is still unavailable. Do not send a duplicate.")); }
    finally { if (mounted.current) setBusy(false); }
  };

  const cancelReview = async () => {
    if (busy || !review) return;
    setBusy(true); setError("");
    try {
      const { data } = await client.delete(`${BASE}/proposals/${review.id}`);
      if (mounted.current) {
        if (data.state === "cancelled") { setReview(null); setReceipt(null); requestKey.current = null; }
        else setReceipt(data);
      }
    } catch (failure) { if (mounted.current) setError(safeError(failure, "Cancellation was not confirmed. Check status before creating another review.")); }
    finally { if (mounted.current) setBusy(false); }
  };

  const selectMode = (next) => {
    if (busy || review) return;
    requestKey.current = null; setReceipt(null); setError(""); setMode(next);
  };
  const flag = mode === "mark_read" || mode === "mark_unread";
  const terminal = receipt && ["accepted", "completed", "failed", "cancelled"].includes(receipt.state);

  return <section data-testid="mail-write-controls" style={{ borderTop: "1px solid #64748b", marginTop: "1rem", paddingTop: "1rem", lineHeight: 1.6 }} aria-busy={busy}>
    <h3 style={{ fontSize: "1.3rem", fontWeight: 700 }}>Email actions</h3>
    {error && <p role="alert">{error}</p>}
    {!cap && !error && <p role="status">Checking email permissions…</p>}
    {cap && !cap.enabled && <>
      <p>Sending, replies and explicit read/unread changes are available. Every action needs a separate review and approval. Opening an email still changes nothing.</p>
      <button type="button" className={button} disabled={busy} onClick={() => setPermission(true)}>Enable sending and read-state changes</button>
    </>}
    {cap && <>
      {!review && !receipt && controlsOnly && <div className="karen-mail-toolbar">
        <button type="button" className={button} disabled={busy} onClick={() => selectMode("reply")}>Reply</button>
        <button type="button" className={button} disabled={busy || !cap.enabled} onClick={() => selectMode("mark_read")}>Mark as read</button>
        <button type="button" className={button} disabled={busy || !cap.enabled} onClick={() => selectMode("mark_unread")}>Mark as unread</button>
      </div>}
      {!review && !receipt && mode && <form onSubmit={prepare} style={{ display: "grid", gap: ".8rem", marginTop: "1rem" }}>
        {flag ? <p>{mode === "mark_read" ? "Mark this email as read?" : "Mark this email as unread?"} The next step shows the exact message for approval.</p> : <>
          <MailDraftAssistant initialInstruction={initialInstruction} initialRequestId={initialRequestId} initialSeen={draftSeen}
            connectionVersion={cap.connection_version} subject={subject} text={body} busy={busy} onBusy={setBusy}
            onDraft={(draft) => { requestKey.current = null; setSubject(draft.subject); setBody(draft.text); setError(""); }} />
          <p>From: <strong>{cap.from}</strong></p>
          <MailRecipient value={to} disabled={busy} onChange={(value) => edit(setTo, value)} />
          <p style={{ fontSize: ".95rem" }}>Check the full address, including any suggested reply address. A sender’s display name does not verify who they are.</p>
          <label>Subject<input aria-label="Subject" style={fieldStyle} required maxLength={300} value={subject} disabled={busy}
            onChange={(event) => edit(setSubject, event.target.value)} autoComplete="off" /></label>
          <label>Message<textarea aria-label="Message" style={fieldStyle} rows={8} required maxLength={50000} value={body} disabled={busy}
            onChange={(event) => edit(setBody, event.target.value)} autoComplete="off" /></label>
          <p style={{ fontSize: ".95rem" }}>One recipient, text only. No attachments or automatic Sent-folder copy in this release. Drafts on this screen are not saved when you leave.</p>
        </>}
        <button type="submit" className={button} disabled={busy || !cap.enabled}>{busy ? "Preparing…" : flag ? "Review change" : "Review email"}</button>
      </form>}
      {review && <div data-testid="mail-exact-review" style={{ marginTop: "1rem" }}>
        <h4 style={{ fontWeight: 700 }}>Review before approval</h4>
        {review.preview.kind.startsWith("mark_") ? <p><strong>{review.preview.kind === "mark_read" ? "Mark as read" : "Mark as unread"}</strong>: {review.preview.subject}</p>
          : <><p>From: <strong>{review.preview.from}</strong><br />To: <strong>{review.preview.to}</strong></p>
            <p>Subject: <strong>{review.preview.subject}</strong></p>
            <div className="karen-mail-text" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{review.preview.text}</div></>}
        <p>This exact review expires after 15 minutes. Changes require a new review.</p>
        <div className="karen-mail-toolbar">
          <button type="button" className={button} disabled={busy || Boolean(receipt && receipt.state !== "prepared")} onClick={approve}>
            {review.preview.kind.startsWith("mark_") ? "Approve this change" : "Send reviewed email"}
          </button>
          {!receipt && <button type="button" className={button} disabled={busy} onClick={cancelReview}>Cancel review / edit</button>}
        </div>
      </div>}
      {receipt && <div role="status" data-testid="mail-operation-receipt">
        <p>{receipt.message || "Check the operation status before taking another action."}</p>
        {!terminal && <button type="button" className={button} disabled={busy} onClick={checkStatus}>Check status — do not resend</button>}
        {terminal && <button type="button" className={button} disabled={busy} onClick={() => {
          setReceipt(null); setReview(null); setBody(""); requestKey.current = null; if (controlsOnly) setMode(null);
        }}>Close receipt</button>}
      </div>}
      {cap.enabled && !review && !receipt && <p><button type="button" className={button} disabled={busy} onClick={() => setPermission(false)}>Disable sending and changes</button></p>}
    </>}
  </section>;
}

export default function MailComposer(props) {
  const { user, loading } = useAuth();
  const account = text(user?.id || user?._id);
  const original = useRef(null);
  if (!loading && account && original.current === null) original.current = account;
  if (loading) return <p role="status">Checking your NDG account…</p>;
  if (!account || !["adi", "pdi", "admin"].includes(user?.role)) return <p role="alert">Sign in to your NDG instructor account.</p>;
  if (original.current !== account) return <p role="alert">Your account changed. Close and reopen the email editor.</p>;
  if (props.sourceId && !validId(props.sourceId)) return <p role="alert">Choose a valid email from search first.</p>;
  return <Composer key={`${account}:${props.sourceId || "new"}`} {...props} />;
}
